import React, { useState, useEffect } from 'react';
import type { User } from '@/types/user';
import { Card, CardContent, CardHeader, CardTitle, SubscriptionCardContent, PricingCardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Check, 
  Star,
  Zap,
  Award,
  FileText,
  Users,
  Calendar,
  X,
  Camera,
  Loader2
} from 'lucide-react';
import { subscriptionService, type UserSubscription, type SubscriptionPackage } from '@/services/subscriptionService';
import { toast } from '@/hooks/use-toast';

interface SubscriptionPlanProps {
  user: User;
}

export const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({ user }) => {
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Get the most recent active subscription (filter by is_active flag)
  const activeSubscription = subscriptions.find(sub => sub.is_active === true) || null;
  
  // Default to 'change' tab initially, will be updated after subscriptions load
  const [activeSubscriptionTab, setActiveSubscriptionTab] = useState('change');

  // Fetch user subscriptions and packages on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchSubscriptions(), fetchPackages()]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Update tab when subscriptions are loaded - show 'current' if user has active subscription
  useEffect(() => {
    if (!isLoading) {
      const hasActiveSubscription = subscriptions.some(sub => sub.is_active === true);
      if (hasActiveSubscription) {
        setActiveSubscriptionTab('current');
      } else {
        setActiveSubscriptionTab('change');
      }
    }
  }, [subscriptions, isLoading]);

  const fetchSubscriptions = async () => {
    try {
      const response = await subscriptionService.getUserSubscriptions();
      setSubscriptions(response.subscriptions || []);
    } catch (error: any) {
      console.error('Failed to fetch subscriptions:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to load subscription data.",
        variant: "destructive",
      });
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await subscriptionService.getPackages();
      setPackages(response.packages || []);
    } catch (error: any) {
      console.error('Failed to fetch packages:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to load subscription packages.",
        variant: "destructive",
      });
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setIsCancelling(true);
    try {
      await subscriptionService.cancelSubscription(subscriptionId);
      toast({
        title: "Success",
        description: "Subscription cancelled successfully.",
      });
      // Refresh subscriptions
      await fetchSubscriptions();
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to cancel subscription.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubscribe = async (plan: any) => {
    // Check if this is a free trial (price is 0 or name contains "Free Trial")
    const isFreeTrial = plan.priceValue === 0 || 
                       plan.price === 'RM0.00' || 
                       plan.price === 'RM0' || 
                       plan.price === 'Free' ||
                       plan.price === 'FREE' ||
                       plan.name.toLowerCase().includes('free trial');

    if (isFreeTrial) {
      // Activate free trial directly without Stripe checkout
      try {
        const response = await subscriptionService.activateFreeTrial({
          subscription_package_id: plan.packageId,
        });

        toast({
          title: "Success",
          description: response.message || "Free trial activated successfully!",
        });

        // Refresh subscriptions to show the new active subscription
        await fetchSubscriptions();
        
        // Switch to current plan tab if user now has an active subscription
        setActiveSubscriptionTab('current');
      } catch (error: any) {
        console.error('Failed to activate free trial:', error);
        toast({
          title: "Error",
          description: error.response?.data?.message || "Failed to activate free trial. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // For paid plans, use Stripe checkout
    if (!plan.stripe_price_id) {
      toast({
        title: "Error",
        description: "Stripe price ID not found for this package.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await subscriptionService.createSubscriptionCheckout({
        price_id: plan.stripe_price_id,
        subscription_package_id: plan.packageId,
        allow_multiple: false,
        source: 'profile',
      });

      // Redirect to Stripe checkout
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Checkout URL not received');
      }
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Format date helper
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Helper function to check if package is PLUS (1-Month Subscription or 3-Months Subscription)
  const isPlusPlan = (packageName: string): boolean => {
    const nameLower = packageName.toLowerCase();
    return nameLower.includes('1-month subscription') || nameLower.includes('3-months subscription');
  };

  // Default subscription data (fallback when no subscription)
  const subscriptionData = activeSubscription ? {
    planName: activeSubscription.package.name,
    planType: isPlusPlan(activeSubscription.package.name) ? 'PLUS' : 'BASIC',
    expiryDate: formatDate(activeSubscription.expire_date),
    daysRemaining: activeSubscription.days_remaining,
    features: [
      { icon: Star, text: 'Unlimited SKILLSTREE', available: true },
      { icon: Users, text: 'Unlimited Expert-Curated Courses', available: true },
      { icon: Award, text: 'Unlock Extra Badges to Highlight Your Skills', available: true },
      { icon: FileText, text: 'Unlock Passport to Showcase Your Expertise', available: true },
      { icon: Zap, text: 'Live Career Coaching Sessions', available: isPlusPlan(activeSubscription.package.name) },
      { icon: Calendar, text: 'Career Networking with Industry Pros', available: isPlusPlan(activeSubscription.package.name) }
    ]
  } : {
    planName: 'No Active Subscription',
    planType: 'FREE',
    expiryDate: 'N/A',
    daysRemaining: 0,
    features: [
      { icon: Star, text: 'Unlimited SKILLSTREE', available: false },
      { icon: Users, text: 'Unlimited Expert-Curated Courses', available: false },
      { icon: Award, text: 'Unlock Extra Badges to Highlight Your Skills', available: false },
      { icon: FileText, text: 'Unlock Passport to Showcase Your Expertise', available: false },
      { icon: Zap, text: 'Live Career Coaching Sessions', available: false },
      { icon: Calendar, text: 'Career Networking with Industry Pros', available: false }
    ]
  };

  // Helper function to get features based on package
  const getPackageFeatures = (pkg: SubscriptionPackage) => {
    const isPlus = isPlusPlan(pkg.name);
    
    // Get quota values from API, default to 0 if not provided
    const careerCoachingQuota = pkg.career_coaching_quota ?? 0;
    const professionalNetworkQuota = pkg.professional_network_building_quota ?? 0;
    
    // Format quota text for career coaching
    const careerCoachingText = careerCoachingQuota > 0 
      ? `${careerCoachingQuota}-time Career \n Coaching Session`
      : 'Career \n Coaching Session';
    
    // Format quota text for professional network building
    const professionalNetworkText = professionalNetworkQuota > 0
      ? `${professionalNetworkQuota}-time Professional \n Network Building`
      : 'Professional \n Network Building';
    
    // Default/Other subscriptions (non-PLUS or other durations)
    if (isPlus) {
      return [
        { text: 'Full Access to All Skillstree Microlearning', available: true },
        { text: careerCoachingText, available: careerCoachingQuota > 0 },
        { text: professionalNetworkText, available: professionalNetworkQuota > 0 },
        { text: 'Earn Microlearning Digital Badge', available: true },
        { text: 'Earn Digital Microlearning Role Passport', available: true }
      ];
    } else {
      return [
        { text: 'Partial Access to One \n Skillstree Microlearning', available: true },
        { text: careerCoachingText, available: careerCoachingQuota > 0 },
        { text: professionalNetworkText, available: professionalNetworkQuota > 0 },
        { text: 'Earn Microlearning Digital Badge', available: true },
        { text: 'Earn Digital Microlearning \n Role Passport', available: true }
      ];
    }
  };

  // Format price to display format
  const formatPrice = (price: number | string): string => {
    // If price is already "Free" or "FREE", return it as-is
    if (typeof price === 'string' && price.toLowerCase() === 'free') {
      return 'Free';
    }
    
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice) || numPrice === 0 || numPrice === 0.00) {
      return 'Free';
    }
    return `RM${numPrice.toFixed(2)}`;
  };

  // Format duration text
  const formatDuration = (validityDays: number): string => {
    const months = Math.round(validityDays / 30);
    if (months === 1) return '1-Month Subscription';
    if (months === 3) return '3-Months Subscription';
    return `${validityDays}-Day Subscription`;
  };

  // Determine if a plan is the current plan based on active subscription
  const isPlanCurrent = (stripePriceId: string | null | undefined) => {
    if (!activeSubscription || !stripePriceId) return false;
    return activeSubscription.package.stripe_price_id === stripePriceId;
  };

  // Transform API packages to component format
  const subscriptionPlans = packages
    .filter(pkg => !pkg.only_for_renewal) // Filter out renewal-only packages
    .map((pkg, index) => {
      const isPlus = isPlusPlan(pkg.name);
      const validityMonths = Math.round(pkg.validity_days / 30);
      // 3-month subscription (around 90 days, give some tolerance: 85-95 days) should be recommended
      const isRecommended = pkg.validity_days >= 85 && pkg.validity_days <= 95;

      let planLogoSrc = `/navbar-logo-latest-white.png`;
      let planLogoAlt = `SKILLSTURE`;
      let planLogoCls = `h-10 sm:h-12`;

      if (isRecommended) {
        planLogoSrc = `/navbar-logo-latest-blue.png`;
        planLogoAlt = `${planLogoAlt} Recommended`;
      }

      if (isPlus) {
        planLogoSrc = isRecommended ? `/navbar-logo-plus-blue.png` : `/navbar-logo-plus-white.png`;
        planLogoAlt = `${planLogoAlt} PLUS`;
        planLogoCls = `-m-4 h-16 sm:h-20`;
      }

      return {
        id: pkg.id.toString(),
        packageId: pkg.id,
        name: pkg.name,
        price: formatPrice(pkg.price),
        priceValue: pkg.price, // Store numeric price for easier checking
        originalPrice: pkg.original_price ? formatPrice(pkg.original_price) : null,
        duration: formatDuration(pkg.validity_days),
        isRecommended: isRecommended,
        isCurrent: isPlanCurrent(pkg.stripe_price_id),
        limitedOffer: pkg.original_price ? 'Limited Time Offer Now!' : undefined,
        features: getPackageFeatures(pkg),
        stripe_price_id: pkg.stripe_price_id,
        planLogoSrc,
        planLogoAlt,
        planLogoCls
      };
    });

  const renderCurrentPlan = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      );
    }

    let activeSubIsPlus = activeSubscription && isPlusPlan(activeSubscription.package.name);

      let activeSubLogoSrc = `/navbar-logo-latest-white.png`;
      let activeSubLogoAlt = `SKILLSTURE`;
      let activeSubLogoCls = `h-10 sm:h-12`;

      if (activeSubIsPlus) {
        activeSubLogoSrc = `/navbar-logo-plus-white.png`;
        activeSubLogoAlt = `${activeSubLogoAlt} PLUS`;
        activeSubLogoCls = `-m-4 h-16 sm:h-20`;
      }
    

    return (
      <div className="space-y-6">
        {/* Current Plan Card */}
        <Card className="rounded-lg shadow-inner border-0" style={{
          boxShadow: 'inset 0 0 10px rgba(96, 96, 96, 1)',
          backgroundColor: '#6A6A6A59'
        }}>
          <SubscriptionCardContent>
            {/* Plan Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              {/* <div className="flex items-center gap-3"> */}
                
                <img 
                  src={activeSubLogoSrc}
                  alt={activeSubLogoAlt}
                  className={`${activeSubLogoCls} mr-2 w-auto object-cover`}
                  onError={(e) => {
                    e.currentTarget.src = '/Skillsture.svg';
                    e.currentTarget.classList = 'h-10 sm:h-12 w-auto object-cover';

                  }}
                />
                {/* {activeSubscription && isPlusPlan(activeSubscription.package.name) && (
                  <div className="skillsture-plus-badge">
                    <span className="text-white text-xs sm:text-sm profile-typography">PLUS</span>
                  </div>
                )} */}
              {/* </div> */}
              {activeSubscription ? (
                <div className="bg-orange-500 text-white px-4 sm:px-6 py-2 rounded-full font-medium text-sm sm:text-base w-full sm:w-auto text-center">
                  Current Plan
                </div>
              ) : (
                <Button 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 sm:px-6 py-2 rounded-full font-medium text-sm sm:text-base w-full sm:w-auto"
                  onClick={() => setActiveSubscriptionTab('change')}
                >
                  Subscribe Now
                </Button>
              )}
            </div>

          {/* Features List */}
          <div className="space-y-3 sm:space-y-4">
            {subscriptionData.features.map((feature, index) => {
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className={`flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {index === 0 && <img src="/tree_1.svg" alt="Tree" style={{ width: '22px', height: '22px' }}/>}
                    {index === 1 && <img src="/camera.svg" alt="Camera" style={{ width: '22px', height: '22px' }}/>}
                    {index === 2 && <img src="/badge.svg" alt="Badge" style={{ width: '22px', height: '22px' }}/>}
                    {index === 3 && <img src="/card.svg" alt="Card" style={{ width: '22px', height: '22px' }}/>}
                    {index === 4 && <img src="/stars.svg" alt="Stars" style={{ width: '22px', height: '22px' }}/>}
                    {index === 5 && <img src="/thumb_up.svg" alt="Thumb Up" style={{ width: '22px', height: '22px' }}/>}
                  </div>
                  <div className="flex items-start gap-2 flex-1">
                    <span 
                      className={`text-sm sm:text-base leading-relaxed ${
                        feature.text === 'Live Career Coaching Sessions' || feature.text === 'Career Networking with Industry Pros'
                          ? ''
                          : 'text-white'
                      }`}
                      style={
                        feature.text === 'Live Career Coaching Sessions' || feature.text === 'Career Networking with Industry Pros'
                          ? { color: '#F96004' }
                          : {}
                      }>
                      {feature.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </SubscriptionCardContent>
      </Card>

      {/* Manage Subscription Card */}
      <Card className="rounded-lg shadow-inner border-0 manage-subscription-card" style={{
        boxShadow: 'inset 0 0 10px rgba(96, 96, 96, 1)',
        backgroundColor: '#6A6A6A59'
      }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 profile-typography">
            Manage Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-white font-medium text-sm sm:text-base">Current Plan</p>
              <p className="text-gray-300 text-xs sm:text-sm">{subscriptionData.planName}</p>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <p className="text-white font-medium text-sm sm:text-base">Plan Expiry Date</p>
              <p className="text-gray-300 text-xs sm:text-sm">{subscriptionData.expiryDate}</p>
            </div>
          </div>

          {/* {activeSubscription && (
            <>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-white font-medium text-sm sm:text-base">Days Remaining</p>
                  <p className="text-gray-300 text-xs sm:text-sm">{subscriptionData.daysRemaining} days</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-600">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-white/10 text-sm h-10"
                    onClick={() => setActiveSubscriptionTab('change')}
                  >
                    Change Plan
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-white/10 text-sm h-10"
                    onClick={() => activeSubscription.stripe_subscription_id && handleCancelSubscription(activeSubscription.stripe_subscription_id)}
                    disabled={isCancelling || !activeSubscription.stripe_subscription_id}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Subscription'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )} */}
        </CardContent>
      </Card>
    </div>
    );
  };

  const renderChangePlan = () => {
    const planCount = subscriptionPlans.length;
    const gridCols = planCount === 1 ? 'grid-cols-1' : planCount === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';
    
    return (
    <div className="space-y-6 pb-4">
      <div className="flex justify-center">
        <div className={`grid grid-cols-1 ${gridCols} gap-4 sm:gap-6 w-full ${planCount <= 2 ? 'max-w-4xl' : 'max-w-6xl'}`}>
          {subscriptionPlans.map((plan, index) => (
          <Card 
            key={plan.id} 
            className={`rounded-lg shadow-inner border-2 relative flex flex-col ${
              plan.isCurrent 
                ? 'border-orange-500' 
                : plan.isRecommended 
                ? 'border-blue-500' 
                : 'border-gray-600'
            }`}
            style={{
              backgroundColor: plan.isCurrent ? '#FF69004D' : plan.isRecommended ? '#FFFFFF' : '#6A6A6A59',
              height: '572px'
            }}
          >
            {plan.isRecommended && !plan.isCurrent && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  RECOMMENDED
                </span>
              </div>
            )}
            
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <img
                  src={plan.planLogoSrc}
                  alt={plan.planLogoAlt}
                  className={`${plan.planLogoCls} w-auto object-cover`}
                  onError={(e) => e.currentTarget.src = '/Skillsture.svg'}
                />
                {/* {isPlusPlan(plan.name) && (
                  <div className="skillsture-plus-badge">
                    <span className="text-white text-xs font-medium">PLUS</span>
                  </div>
                )} */}
              </div>
              
              <div className="h-4 sm:h-6 flex items-center justify-center">
                {plan.limitedOffer && index !== 0 && (
                  <p className={`text-xs sm:text-sm ${plan.isCurrent ? 'text-white' : plan.isRecommended ? 'text-black' : 'text-white'}`}>{plan.limitedOffer}</p>
                )}
              </div>
              
              <div className="text-center mt-3 sm:mt-4">
                <div className="flex items-center justify-center gap-2">
                  <span className={`text-xl sm:text-2xl lg:text-3xl font-semibold ${plan.isCurrent ? 'text-white' : plan.isRecommended ? 'text-black' : 'text-white'}`}>{plan.price}</span>
                  {plan.id !== 'basic' && (plan.price === 'RM89.00' || plan.price === 'RM99.00')}
                </div>
                <div className="h-4 sm:h-5 flex items-center justify-center">
                  {plan.originalPrice && plan.price !== 'FREE' && plan.price !== 'Free' && (
                    <div className="text-gray-400 text-xs sm:text-sm line-through">{plan.originalPrice}</div>
                  )}
                </div>
                <div className={`text-xs sm:text-sm mt-1 ${plan.isCurrent ? 'text-white' : plan.isRecommended ? 'text-black' : 'text-white'}`}>{plan.duration}</div>
              </div>
            </CardHeader>
            
            <PricingCardContent className="flex-1 flex flex-col px-4 sm:px-6">
              <div className="space-y-2 sm:space-y-3 flex-1">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      feature.available 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {feature.available ? (
                        <Check className="w-3 h-3 sm:w-4 sm:h-4"/>
                      ) : (
                        <X className="w-3 h-3 sm:w-4 sm:h-4"/>
                      )}
                    </div>
                    <span className={`text-xs sm:text-sm leading-relaxed ${
                      feature.available 
                        ? (plan.isCurrent ? 'text-white' : plan.isRecommended ? 'text-black' : 'text-white')
                        : 'text-gray-400 line-through'
                    }`}>
                      {feature.text.replace(/\n/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="pt-6 sm:pt-8 lg:pt-12 mt-auto">
                <Button 
                  onClick={() => {
                    if (plan.isCurrent && activeSubscription) {
                      // Do nothing for current plan
                      return;
                    }
                    handleSubscribe(plan);
                  }}
                  disabled={!!(plan.isCurrent && activeSubscription)}
                  className={`w-full h-8 sm:h-9 text-xs sm:text-sm ${
                    plan.isCurrent 
                      ? 'bg-orange-500 hover:bg-orange-600 !text-white' 
                      : plan.isRecommended
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                  style={plan.isCurrent && activeSubscription ? { color: '#FFFFFF' } : undefined}
                >
                  {plan.isCurrent && activeSubscription ? 'Current Plan' : (plan.priceValue === 0 || plan.price === 'RM0.00' || plan.price === 'RM0' || plan.price === 'Free' || plan.price === 'FREE' || plan.name.toLowerCase().includes('free')) ? 'Get started Free' : 'Subscribe Now'}
                </Button>
              </div>
            </PricingCardContent>
          </Card>
        ))}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="space-y-6 pb-8 sm:pb-12">
      {/* Tab Navigation - Only show if user has active subscription */}
      {activeSubscription && !isLoading && (
        <div className="flex rounded-full p-1 subscription-tab-container w-full max-w-full overflow-hidden">
          <button
            onClick={() => setActiveSubscriptionTab('current')}
            className={`flex-1 px-3 sm:px-6 py-2 sm:py-3 rounded-full font-medium flex items-center justify-center text-xs sm:text-sm lg:text-base ${
              activeSubscriptionTab === 'current'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 bg-transparent'
            }`}
          >
            <span className="hidden sm:inline">Current Plan & Settings</span>
            <span className="sm:hidden">Current Plan</span>
          </button>
          <button
            onClick={() => setActiveSubscriptionTab('change')}
            className={`flex-1 px-3 sm:px-6 py-2 sm:py-3 rounded-full font-medium flex items-center justify-center text-xs sm:text-sm lg:text-base ${
              activeSubscriptionTab === 'change'
                ? 'bg-orange-500 text-white'
                : 'text-gray-300 bg-transparent'
            }`}
          >
            Change Plan
          </button>
        </div>
      )}

      {/* Tab Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : !activeSubscription ? (
        // If no subscription, show heading and subscription plans
        <div className="space-y-6">
          <h2 className="text-white text-2xl sm:text-3xl font-semibold text-start">Choose your plan</h2>
          {renderChangePlan()}
        </div>
      ) : activeSubscriptionTab === 'current' ? (
        // If has subscription and on current tab, show current plan
        renderCurrentPlan()
      ) : (
        // If has subscription and on change tab, show plans
        renderChangePlan()
      )}
    </div>
  );
};
